import React, { useId } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

/**
 * Controls shared by every settings page (spec 15 §3.9).
 *
 * - Toggles are `<button role="switch" aria-checked>`.
 * - Checkboxes are real `<input type="checkbox">` inside a `<label>`.
 * - Three-state controls are a radio group, not three buttons.
 *
 * The switch is green when on, which is what the boards draw — the shared
 * `SwitchControl` is indigo and is used elsewhere, so it is left alone.
 *
 * Styling follows `SETTINGS-STYLE-GUIDE.md` (2 Oct). The load-bearing change is
 * §3: a field is **one bordered shell** with a transparent input inside it, so a
 * unit (`min`, `calls/day`) can be welded to the field rather than floating
 * beside it — and so the focus ring is drawn on the shell, around both.
 */

/**
 * The field shell (§1.3, §3). Border one step darker than `--border-default` so a
 * field reads as a field on a white card; the ring is on `focus-within` because
 * the bordered element is the wrapper, not the input.
 */
const FIELD_SHELL = cn(
  'inline-flex items-stretch overflow-hidden rounded-[10px]',
  'border border-[#dfe4ee] bg-[var(--surface-card)]',
  'transition-[border-color,box-shadow] duration-150',
  'hover:border-[#ccd4e4]',
  'focus-within:border-[var(--brand-indigo)] focus-within:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]',
);

/** The same border + ring for controls that are a single element (select, textarea). */
const FIELD_SELF = cn(
  'rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)] text-[var(--text-primary)] outline-none',
  'transition-[border-color,box-shadow] duration-150',
  'hover:border-[#ccd4e4]',
  'focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]',
);

export function SetSwitch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  /** Accessible name when the row's own label is not programmatically linked. */
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange?.(!checked);
      }}
      className={cn(
        'inline-flex h-[26px] w-[44px] shrink-0 rounded-full p-[3px] transition-colors outline-none',
        'focus-visible:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]',
        checked ? 'justify-end bg-[var(--money-green)]' : 'justify-start bg-[#D8DCE7]',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <span className="block h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(36,41,54,0.25)]" />
    </button>
  );
}

/** A real checkbox in a label — used for the eight roles and the insight checks. */
export function SetCheck({
  checked,
  onChange,
  children,
  disabled,
  tone = 'pill',
}: {
  checked: boolean;
  onChange?: (next: boolean) => void;
  children: React.ReactNode;
  disabled?: boolean;
  /** `pill` is the bordered role chip; `plain` is a bare checkbox + label. */
  tone?: 'pill' | 'plain';
}) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 text-[12.5px] font-semibold',
        tone === 'pill' && 'rounded-[10px] border px-3 py-2',
        tone === 'pill' &&
          (checked
            ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]'
            : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)]'),
        tone === 'plain' && (checked ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'),
        disabled && 'cursor-not-allowed opacity-55',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => { onChange?.(e.target.checked); }}
        className="h-[15px] w-[15px] shrink-0 accent-[var(--brand-indigo)]"
      />
      <span className="min-w-0">{children}</span>
    </label>
  );
}

/** Three states, as a radio group — never collapsed into a boolean (§15). */
export function SetTriState<T extends string>({
  value,
  options,
  onChange,
  name,
}: {
  value: T;
  options: { value: T; label: string; tone?: 'green' | 'amber' | 'muted'; disabled?: boolean }[];
  onChange?: (next: T) => void;
  name?: string;
}) {
  const auto = useId();
  const groupName = name ?? auto;
  // §6: a white bordered shell — the control reads as a field, which is what it is.
  return (
    <div
      role="radiogroup"
      className="inline-flex items-center gap-[2px] rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)] p-[3px]"
    >
      {options.map((o) => {
        const active = o.value === value;
        const tone =
          o.tone === 'green'
            ? { color: 'var(--money-green)', background: '#EEF6F2' }
            : o.tone === 'amber'
              ? { color: 'var(--warn-amber)', background: '#FBF3E4' }
              : { color: 'var(--text-muted)', background: '#F1F3F9' };
        return (
          <label
            key={o.value}
            className={cn(
              'rounded-[7px] px-3 py-[5px] text-[12px] font-bold',
              o.disabled
                ? 'cursor-not-allowed text-[var(--text-muted)] opacity-50'
                : cn('cursor-pointer', !active && 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'),
            )}
            style={active && !o.disabled ? tone : undefined}
          >
            <input
              type="radio"
              name={groupName}
              checked={active}
              disabled={o.disabled}
              onChange={() => { if (!o.disabled) onChange?.(o.value); }}
              className="sr-only"
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/** A locked row's right-hand side — shown, not hidden, because the practice asks (§3.6). */
export function SetLocked({ label = 'Locked' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--text-muted)]">
      <Icon kind="lock" size={12} stroke={2} />
      {label}
    </span>
  );
}

/**
 * `Blocked` — a control that cannot act because of an external dependency
 * (spec 16 §12.3: A2P registration, ELVA Pay).
 *
 * **`Off` is a choice. `Blocked` is a fact. They must not look the same.** A
 * plain off toggle here says the practice decided this; a lock with the
 * dependency named says the world did.
 */
export function SetBlocked({ reason, word = 'Blocked' }: { reason: string; word?: string }) {
  return (
    <span
      title={reason}
      className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#FBF3E4] px-2.5 py-1.5 text-[11.5px] font-semibold text-[var(--warn-amber)]"
    >
      <Icon kind="alert" size={12} stroke={2.2} />
      {word}
    </span>
  );
}

/**
 * A slider that reads out in words rather than as a number (§5.1). `2 attempts
 * to reschedule` is a sentence a practice can agree or disagree with; `2` is a
 * number they have to interpret.
 */
export function SetSlider({
  value,
  min,
  max,
  onChange,
  readOut,
  minLabel,
  maxLabel,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  onChange?: (next: number) => void;
  readOut: (n: number) => string;
  minLabel: string;
  maxLabel: string;
  ariaLabel: string;
}) {
  return (
    <div className="mt-3 w-full max-w-[420px]">
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        aria-label={ariaLabel}
        aria-valuetext={readOut(value)}
        onChange={(e) => { onChange?.(Number(e.target.value)); }}
        className="w-full accent-[var(--brand-indigo)]"
      />
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="text-[10.5px] text-[var(--text-muted)]">{minLabel}</span>
        <span className="text-[12.5px] font-bold text-[var(--brand-indigo)]">{readOut(value)}</span>
        <span className="text-[10.5px] text-[var(--text-muted)]">{maxLabel}</span>
      </div>
    </div>
  );
}

/**
 * A two-state segmented control. Distinct from `SetTriState` only in arity, but
 * used where the board draws two buttons (`Soft guilt` / `Strict policy`,
 * `Every case` / `By reason`).
 */
export function SetSegmented<T extends string>({
  value,
  options,
  onChange,
  name,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange?: (next: T) => void;
  name?: string;
  ariaLabel?: string;
}) {
  const auto = useId();
  const groupName = name ?? auto;
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-[2px] rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)] p-[3px]"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              'cursor-pointer rounded-[7px] px-3 py-1.5 text-[12.5px] font-semibold',
              active
                ? 'bg-[var(--brand-indigo)] text-white'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            <input
              type="radio"
              name={groupName}
              checked={active}
              onChange={() => { onChange?.(o.value); }}
              className="sr-only"
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/**
 * An ordered list the practice can reorder — the channel order on Gap Filler and
 * the TC. Up/down buttons rather than drag, because the list is three long and a
 * keyboard user should be able to do it.
 */
export function SetOrderedChips({
  items,
  onReorder,
  ariaLabel,
}: {
  items: string[];
  onReorder?: (next: string[]) => void;
  ariaLabel: string;
}) {
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    const a = next[i];
    const b = next[j];
    if (a === undefined || b === undefined) return;
    next[i] = b;
    next[j] = a;
    onReorder?.(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label={ariaLabel}>
      {items.map((item, i) => (
        <span
          key={item}
          role="listitem"
          className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#F1F3F9] px-2.5 py-1.5 text-[12.5px] font-semibold text-[var(--text-secondary)]"
        >
          <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{i + 1}</span>
          {item}
          {onReorder ? (
            <span className="ml-0.5 inline-flex flex-col leading-none">
              <button
                type="button"
                aria-label={`Move ${item} earlier`}
                disabled={i === 0}
                onClick={() => { move(i, -1); }}
                className="text-[9px] text-[var(--text-muted)] disabled:opacity-30"
              >
                ▲
              </button>
              <button
                type="button"
                aria-label={`Move ${item} later`}
                disabled={i === items.length - 1}
                onClick={() => { move(i, 1); }}
                className="text-[9px] text-[var(--text-muted)] disabled:opacity-30"
              >
                ▼
              </button>
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function SetInput({
  value,
  onChange,
  placeholder,
  mono,
  prefix,
  suffix,
  align,
  disabled,
  className,
  ariaLabel,
}: {
  value: string;
  onChange?: (next: string) => void;
  placeholder?: string;
  mono?: boolean;
  /** A fixed leading glyph such as `$`. Sits outside the shell — it labels the
   * value, it is not part of it (§3.6). */
  prefix?: string;
  /** A unit welded to the field: `min`, `a day`, `calls/day` (§3). */
  suffix?: string;
  /** Numbers right-align and go semibold; prose stays left and regular (§3.3). */
  align?: 'left' | 'right';
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  const numeric = align === 'right';
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {prefix ? <span className="shrink-0 text-[13px] text-[var(--text-muted)]">{prefix}</span> : null}
      <span
        className={cn(
          FIELD_SHELL,
          'min-w-0 flex-1',
          disabled && 'border-[var(--border-default)] bg-[var(--surface-sunken)] hover:border-[var(--border-default)]',
        )}
      >
        <input
          value={value}
          aria-label={ariaLabel}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => { onChange?.(e.target.value); }}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3.5 py-[9px] text-[14px] text-[var(--text-primary)] outline-none',
            'placeholder:text-[var(--text-muted)]',
            mono && 'font-[var(--font-mono)]',
            numeric && 'text-right font-semibold',
            disabled && 'cursor-not-allowed text-[var(--text-muted)]',
          )}
        />
        {suffix ? (
          <span className="flex shrink-0 items-center border-l border-[#e9edf5] bg-[var(--surface-sunken)] px-3 text-[12.5px] text-[var(--text-muted)]">
            {suffix}
          </span>
        ) : null}
      </span>
    </span>
  );
}

export function SetTextarea({
  value,
  onChange,
  rows = 3,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange?: (next: string) => void;
  rows?: number;
  placeholder?: string;
  ariaLabel?: string;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => { onChange?.(e.target.value); }}
      /* §3: roomier than an input and a 1.85 leading — a rules editor is read a
         paragraph at a time, and it has to stay scannable. */
      className={cn(
        FIELD_SELF,
        'w-full resize-y !rounded-[12px] px-4 py-3.5 text-[14.5px] leading-[1.85] placeholder:text-[var(--text-muted)]',
      )}
    />
  );
}

export function SetSelect<T extends string>({
  value,
  options,
  optionGroups,
  onChange,
  ariaLabel,
  className,
}: {
  value: T;
  options?: { value: T; label: string }[];
  /** When given, renders `<optgroup>`s instead of a flat list (e.g. voices by accent). */
  optionGroups?: { label: string; options: { value: T; label: string }[] }[];
  onChange?: (next: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => { onChange?.(e.target.value as T); }}
      className={cn(FIELD_SELF, 'px-3.5 py-[9px] text-[14px]', className)}
    >
      {optionGroups
        ? optionGroups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))
        : (options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
    </select>
  );
}

/** An alias chip — "what patients call it". */
export function SetChip({
  children,
  tone = 'indigo',
}: {
  children: React.ReactNode;
  tone?: 'indigo' | 'grey' | 'amber' | 'green';
}) {
  const tones = {
    indigo: 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]',
    grey: 'bg-[#F1F3F9] text-[var(--text-secondary)]',
    amber: 'bg-[#FBF3E4] text-[var(--warn-amber)]',
    green: 'bg-[#EEF6F2] text-[var(--money-green)]',
  } as const;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', tones[tone])}>
      {children}
    </span>
  );
}

/** A provider's round monogram, coloured from the roster. */
export function SetAvatar({ initials, colour, size = 32 }: { initials: string; colour: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white"
      style={{ width: size, height: size, background: colour, fontSize: size <= 24 ? 9 : 11.5 }}
    >
      {initials}
    </span>
  );
}
